import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { apiClient } from '@/services/api/client'
import { authApi } from '@/services/api/auth'
import { cookieAuthEnabled } from '@/services/cookieAuthFlag'
import type { User, UserRole, TenantMembership, RefreshTokenResponse, LoginResponse, LoginOTPChallenge } from '@/types/auth'
import { ROLE_PERMISSIONS, isLoginOTPChallenge, specialistRolesOf } from '@/types/auth'
import { clearAllApprovalResumeState } from '@/lib/approvalResumeStorage'
import { clearSessionActivity, recordSessionActivity, recordTenantSwitch } from '@/lib/sessionActivity'
import { resetClientState } from '@/lib/queryClient'
import { createStaleIdentityError, getIdentityGeneration, isStaleIdentityError } from '@/services/api/identityEpoch'
import { isRateLimited, isTransientAuthError, retryTransient } from '@/services/api/transientAuthError'

interface AuthState {
  // State
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  _initialized: boolean // Track if initialization has been attempted
  tenantMemberships: TenantMembership[]
  pendingTenantSelection: boolean
  // Server-authoritative auth method for the current session, sourced from
  // GET /auth/me's `auth_method` field. Drives logout()'s endpoint choice
  // (SSO sessions must be revoked via /sso/logout) and UI branches (e.g.
  // hiding password/MFA settings for SSO users). Deliberately NOT derived by
  // decoding the JWT client-side: cookie-only SSO sessions have no
  // JS-readable token, and single-tenant auto-switch-tenant re-mints a
  // local-issuer token even for SSO users. Defaults to null until the first
  // successful /auth/me response; treat null/absent as 'local' for gating.
  authMethod: 'sso' | 'local' | null

  // Actions
  /**
   * Password step. Resolves null when signed in, or with the challenge when
   * the server emailed a one-time code (finish with verifyLoginOtp). The
   * challenge is deliberately NOT kept in this persisted store.
   */
  login: (email: string, password: string) => Promise<LoginOTPChallenge | null>
  /** Second step: exchange the emailed code for the session. */
  verifyLoginOtp: (challengeToken: string, code: string) => Promise<void>
  /**
   * Ends the session. The LOCAL sign-out (state, caches, token) is synchronous
   * and always happens; the returned promise settles once the server-side
   * revoke calls have finished (or LOGOUT_SERVER_WAIT_MS elapsed). Callers
   * that navigate away MUST await it: a navigation aborts in-flight requests.
   * `serverRevoked` is false when the server did not confirm the revoke.
   */
  logout: () => Promise<LogoutOutcome>
  refreshSession: () => Promise<void>
  ssoRefreshSession: () => Promise<void>
  updateUser: (user: Partial<User>) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  hasPermission: (permission: string) => boolean
  hasRole: (role: UserRole | UserRole[]) => boolean
  initialize: () => Promise<void>
  setTenantMemberships: (memberships: TenantMembership[]) => void
  selectTenant: (tenantId: string) => Promise<void>
  switchTenant: (tenantId: string) => Promise<void>
}

/** Longest sign-out waits for the server to confirm the revoke before the caller navigates anyway. */
export const LOGOUT_SERVER_WAIT_MS = 8_000

export interface LogoutOutcome {
  serverRevoked: boolean
}

/**
 * Decides whether the server ENDED the session, from the three sign-out calls.
 * Truth table per call (SSO and password sessions use the same three calls;
 * each is a no-op on the server when it does not apply to the session type):
 *
 *   POST   /sso/logout   (needs a live access credential, behind mw.Auth)
 *   DELETE /sso/refresh  (public, authenticated by the refresh cookie alone)
 *   POST   /auth/logout  (public; revokes the password refresh family + jti)
 *
 *   result                              | counts as
 *   ------------------------------------+-------------------------------------------
 *   2xx (any call)                      | ended
 *   401 on /sso/logout or /auth/logout  | ended: the access token is already expired
 *                                       |   or blacklisted (idle timeout, other tab),
 *                                       |   so there is no live access session left
 *                                       |   to end through that route; the refresh
 *                                       |   family is ended by DELETE /sso/refresh
 *                                       |   and /auth/logout, whose outcome decides
 *   401 on DELETE /sso/refresh          | NOT ended (it is public; 401 is unexpected)
 *   503 / other 5xx / 429 / 4xx         | NOT ended: the server reported it could not
 *   network error / timeout / abort     | NOT ended: unconfirmed
 *
 * serverRevoked is true only when no call is in the NOT-ended rows.
 */
export function logoutCallEnded(call: 'ssoLogout' | 'ssoRefreshRevoke' | 'authLogout', result: PromiseSettledResult<unknown>): boolean {
  if (result.status === 'fulfilled') return true
  const status = (result.reason as { status?: number; response?: { status?: number } } | undefined)
  const code = status?.status ?? status?.response?.status
  return code === 401 && call !== 'ssoRefreshRevoke'
}

/** The sign-out in flight, if any: a second click joins it instead of aborting it (resetClientState aborts in-flight requests). */
let logoutInFlight: Promise<LogoutOutcome> | null = null

/** Longest Retry-After a refresh will wait out; beyond it the session is kept and the next trigger retries. */
const REFRESH_MAX_RETRY_AFTER_MS = 10_000

// One in-flight /auth/refresh per tab: concurrent callers (boot, 401 recovery,
// several queries) share it instead of each presenting the same refresh token,
// which the server only tolerates once within its reuse-grace window. Other tabs
// have their own store and are not coordinated; they share the refresh cookie,
// so two tabs refreshing at once is the one sibling the grace window allows.
//
// The in-flight promise is keyed by the identity generation: after a logout or
// identity change (which bumps it) a new caller never joins the previous
// identity's refresh, and refreshSession drops that refresh's result instead of
// applying one user's tokens to the next user's session.
let inflightRefresh: { generation: number; promise: Promise<RefreshTokenResponse> } | null = null
function singleFlightRefresh(run: () => Promise<RefreshTokenResponse>): Promise<RefreshTokenResponse> {
  const generation = getIdentityGeneration()
  if (!inflightRefresh || inflightRefresh.generation !== generation) {
    const promise: Promise<RefreshTokenResponse> = run().finally(() => {
      if (inflightRefresh?.promise === promise) inflightRefresh = null
    })
    inflightRefresh = { generation, promise }
  }
  return inflightRefresh.promise
}

// adoptLoginResponse installs a successful LoginResponse — after a password-only
// sign-in or after the email OTP step — including the single-tenant auto-switch.
async function adoptLoginResponse(
  set: (partial: Partial<AuthState>) => void,
  response: LoginResponse,
): Promise<void> {
  // Identity change: abort + drop the previous identity's requests,
  // cache, tenant-scoped stores and drafts BEFORE adopting the new
  // token (#301).
  resetClientState('login')
  // Set token in API client (backend sends 'accessToken')
  apiClient.setToken(response.accessToken)
  // The server's idle window starts with the new session.
  recordSessionActivity(response.idleTimeout)

  const availableTenants = response.availableTenants || []
  const requiresTenantSelection = response.requiresTenantSelection ?? false

  // Single-tenant users skip the tenant selector UI but still need a
  // tenant-scoped token. Without switchTenant the stored token has no
  // tenant_id segment, causing 400 "Tenant context required" on every
  // /api/v1/tenant/* call from the TenantContext middleware.
  // Platform admins are exempt: their token is already platform-scoped
  // and they must not be downgraded to a tenant-scoped token on login.
  if (!requiresTenantSelection && availableTenants.length === 1 && response.user?.role !== 'platform_admin') {
    try {
      const switchResp = await authApi.switchTenant({ tenantId: availableTenants[0].tenantId })
      apiClient.setToken(switchResp.accessToken)
      recordTenantSwitch(switchResp.idleTimeout)
      set({
        user: switchResp.user,
        token: switchResp.accessToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        _initialized: true,
        tenantMemberships: availableTenants,
        pendingTenantSelection: false,
        // This is the password-login flow — the resulting session is
        // local regardless of any re-mint. The next initialize()/me()
        // call will correct this if it's ever wrong.
        authMethod: 'local',
      })
      return
    } catch {
      // switchTenant failed — fall through to the no-tenant token so
      // the user can still navigate (they'll see 400 on tenant routes).
    }
  }

  set({
    user: response.user,
    token: response.accessToken,
    refreshToken: response.refreshToken,
    isAuthenticated: true,
    isLoading: false,
    error: null,
    _initialized: true, // Fresh login = already validated, skip /auth/me wait
    tenantMemberships: availableTenants,
    pendingTenantSelection: requiresTenantSelection,
    authMethod: 'local',
  })
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _initialized: false,
      tenantMemberships: [],
      pendingTenantSelection: false,
      authMethod: null,

      // Login action
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          const response = await authApi.login({ email, password })
          if (isLoginOTPChallenge(response)) {
            set({ isLoading: false, error: null })
            return response
          }
          await adoptLoginResponse(set, response)
          return null
        } catch (err) {
          const error = err as { message?: string }
          set({
            isLoading: false,
            error: error.message || 'Login failed',
          })
          throw err
        }
      },

      verifyLoginOtp: async (challengeToken: string, code: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.verifyLoginOtp(challengeToken, code)
          await adoptLoginResponse(set, response)
        } catch (err) {
          // The OTP form renders its own error from err.code; don't also put
          // it in the shared banner.
          set({ isLoading: false })
          throw err
        }
      },

      // Logout action
      logout: (): Promise<LogoutOutcome> => {
        // Single-flight: a double-click (or idle timeout racing a click) must not
        // start a second logout, whose resetClientState would abort the first
        // one's in-flight revoke calls.
        if (logoutInFlight) return logoutInFlight
        const run = (async (): Promise<LogoutOutcome> => {
        // Captured before the sweep below clears it: /auth/logout blacklists the
        // presented access token's jti, which it can only read from this header.
        const priorToken = get().token

        // Every logout path (manual, idle, 401 expiry, failed refresh) lands
        // here: abort + drop the previous identity's requests, cache and
        // scoped state BEFORE the token goes away (#301).
        resetClientState('logout')
        // Clear token from API client
        apiClient.setToken(null)
        // Sweep persisted chat approval-resume state (user content) so it
        // cannot outlive the session in a shared browser tab (security W3 P2).
        clearAllApprovalResumeState()
        // No session, no idle window to warn about (this tab and the others).
        clearSessionActivity()

        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          _initialized: false, // Reset so next login will initialize
          tenantMemberships: [],
          pendingTenantSelection: false,
          authMethod: null,
        })

        // Clear additional localStorage caches
        localStorage.removeItem('vharta-platform-context') // Platform admin tenant selection
        localStorage.removeItem('vharta-auth-token') // Redundant but explicit

        // Clear the zustand persist blob SYNCHRONOUSLY. The middleware's own
        // subscriber flushes asynchronously (see PersonaSidebar's documented
        // workaround for the same behaviour), and callers navigate to /login on
        // the very next line without waiting — so a hard reload or new tab in
        // that window rehydrates {isAuthenticated: true, user: <previous
        // user>} and the app boots as somebody who just signed out. That is
        // how a second person typing THEIR email landed in the first person's
        // session. Removing the key here closes the window regardless of when
        // the middleware gets around to writing.
        localStorage.removeItem('vharta-auth')

        // Revoke server-side on all three routes unconditionally (awaited by navigating callers, below).
        // Rationale (defense-in-depth): client-side session-type detection is
        // not fully reliable (cookie-only SSO sessions expose no JS-readable
        // token; a single-tenant re-mint can make an SSO user look local), so
        // rather than branch and risk revoking nothing, we always call both.
        // Both handlers no-op safely when not applicable: /sso/logout resolves
        // its jti from the vharta_session cookie and no-ops when there is no
        // SSO session; /auth/logout no-ops without a local refresh token. The
        // `authMethod` state is retained for UI gating only, not for logout.
        // See docs/plans/AUTH_SESSION_HARDENING.md (A4/WS-5).
        // Awaited by navigating callers, bounded so a hung server cannot trap
        // the user on the page; the server finishes the revoke on its own
        // (detached context) even when the wait times out and aborts.
        const opts = {
          signal: AbortSignal.timeout(LOGOUT_SERVER_WAIT_MS),
          headers: priorToken ? { Authorization: `Bearer ${priorToken}` } : undefined,
        }
        // Never rejects: the local sign-out above already happened, and the
        // callers that do not await (refresh failure, 401 expiry) must not leak
        // an unhandled rejection.
        let serverRevoked = false
        try {
          const results = await Promise.allSettled([authApi.ssoLogout(opts), authApi.ssoRefreshRevoke(opts), authApi.logout(opts)])
          serverRevoked =
            logoutCallEnded('ssoLogout', results[0]) &&
            logoutCallEnded('ssoRefreshRevoke', results[1]) &&
            logoutCallEnded('authLogout', results[2])
          if (!serverRevoked) {
            console.error('sign-out: the server did not confirm session revocation', results)
          }
        } catch (err) {
          console.error('sign-out: could not call the server', err)
        }
        return { serverRevoked }
        })()
        logoutInFlight = run
        void run.finally(() => {
          if (logoutInFlight === run) logoutInFlight = null
        })
        return run
      },

      // Refresh session action
      refreshSession: async () => {
        const { refreshToken, user } = get()
        const generationAtStart = getIdentityGeneration()
        // Attempt the refresh even with no in-memory refreshToken.
        //
        // The refresh token is deliberately never persisted to localStorage (XSS
        // mitigation) and that stays true — but it is NOT unavailable after a
        // reload. The backend writes an HttpOnly, SameSite=Strict, path-scoped
        // refresh cookie on every local login (auth/session_cookies.go
        // setRefreshCookie, called from handler.go:687) and the refresh endpoint
        // reads it whenever the request body's refreshToken is empty
        // (handler.go:1121). apiClient sends credentials:'include'.
        //
        // The previous guard threw before trying, so that fully-built path was
        // never exercised in bearer mode: any reload more than ~8-15 minutes
        // after login (privileged roles get the shorter TTL) forced a full
        // re-login with no recovery attempt. Nothing is stored client-side by
        // trying — if the cookie is absent or expired the server answers 401 and
        // the catch below logs out exactly as before.

        try {
          // Only a 429 is retried. The rate limiter answers before the handler
          // runs, so the refresh token was not consumed. A 5xx, timeout or lost
          // response may have rotated it server-side; a blind retry then replays
          // the old token and can trip reuse detection (family revoke, internal/
          // domain/auth/service.go grace window), so those are not retried here:
          // the session is kept and the next natural trigger tries again. A
          // Retry-After beyond REFRESH_MAX_RETRY_AFTER_MS is not waited out either.
          // Concurrent callers share one refresh (single-flight below).
          const response = await singleFlightRefresh(() =>
            retryTransient(
              () =>
                authApi.refreshToken({
                  refreshToken: refreshToken ?? '',
                  currentTenantId: user?.tenantId,
                }),
              { retryable: isRateLimited, giveUpAboveMs: REFRESH_MAX_RETRY_AFTER_MS },
            ),
          )

          // The identity changed (logout, login as someone else) while the refresh
          // was in flight: these tokens belong to the previous identity. Drop them.
          if (getIdentityGeneration() !== generationAtStart) throw createStaleIdentityError()

          // Update token in API client (backend sends 'accessToken')
          apiClient.setToken(response.accessToken)
          // A successful refresh restarts the server's idle window.
          recordSessionActivity(response.idleTimeout)

          set({
            token: response.accessToken,
            refreshToken: response.refreshToken,
          })

          // Reconcile `user` against the server. RefreshTokenResponse is
          // token-only (no user object), so without this a server-side role
          // change (e.g. an admin downgrading this user's persona) never
          // reached the client until the NEXT full reload's initialize()→
          // /auth/me call — every refresh cycle in between kept serving the
          // stale persisted `user`, so a downgraded user could keep seeing
          // admin-only UI for up to a full token TTL. Best-effort: a failure
          // here doesn't fail the refresh itself (the new token IS valid),
          // it just leaves `user` stale for one more cycle, exactly as
          // before this change.
          //
          // Race guard: capture the token we just set, and only apply the
          // fetched user if that token is STILL current when /auth/me
          // resolves. Without this, a concurrent switchTenant() (or another
          // refreshSession(), or logout()) that completes while /auth/me is
          // in flight gets clobbered — this reconciliation's response was
          // fetched under the pre-switch (tenant A) scope, so a stale tenant
          // A user could land on top of a freshly-switched tenant B token,
          // desyncing token and user. Cheap because it's a same-tick string
          // comparison, not a generation counter — the token itself already
          // changes on every event that should invalidate this reconciliation.
          const tokenAtRefresh = response.accessToken
          try {
            const fetchedUser = await authApi.me()
            if (get()._initialized && get().token === tokenAtRefresh) {
              set({ user: fetchedUser })
            }
          } catch {
            // Non-fatal — see comment above.
          }
        } catch (err) {
          // An aborted refresh (identity changed mid-flight) says nothing
          // about the NEW session: do not sign it out (#301).
          if (isStaleIdentityError(err)) throw err
          // A 429/5xx/network failure (after the retries above) proves nothing
          // about the session. logout() would call /sso/logout and /auth/logout
          // and revoke a live session, so leave it intact; the caller decides
          // whether to try again later.
          if (isTransientAuthError(err)) throw err
          // Clear auth state on refresh failure
          get().logout()
          throw err
        }
      },

      // SSO silent refresh (WS-11): exchange the HttpOnly vharta_sso_rt cookie for
      // a fresh vharta_session cookie. Cookie-based, so nothing is stored
      // client-side. Deliberately does NOT logout on failure — apiClient's
      // (services/api/client.ts) 401 handling probes /auth/me to decide
      // teardown, so a transient refresh failure (or the endpoint not existing
      // yet) degrades to the current logout-on-real-expiry behavior rather
      // than force-logging-out a live session.
      ssoRefreshSession: async () => {
        await authApi.ssoRefresh()
      },

      // Update user action
      updateUser: (userData: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }))
      },

      // Set loading state
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      // Set error state
      setError: (error: string | null) => set({ error }),

      // Check if user has a specific permission
      hasPermission: (permission: string) => {
        const { user } = get()
        if (!user) return false

        // Check user's permissions directly
        if (user.permissions?.includes(permission)) return true

        // Check role-based permissions
        const rolePermissions = ROLE_PERMISSIONS[user.role] || []
        return rolePermissions.includes(permission)
      },

      // Check if user has a specific role.
      //
      // Matches the PRIMARY role OR any composable approval persona in
      // additional_roles. Before the union (RBAC sweep qfq/R-1) this was
      // `roles.includes(user.role)`, which made every guard naming
      // approval_approver / approval_viewer unreachable — the personas live in
      // tenant_users.additional_roles, never in `role`, so App.tsx's
      // approval-gates RequireRole set and navigation.ts's tab predicate were
      // both dead code while the server authorized those callers.
      //
      // Absent additionalRoles is treated as "no personas", so this still
      // fails closed when the server says nothing.
      hasRole: (role: UserRole | UserRole[]) => {
        const { user } = get()
        if (!user) return false

        const roles = Array.isArray(role) ? role : [role]
        if (roles.includes(user.role)) return true
        // specialistRolesOf drops anything outside the persona pair, mirroring
        // the server's own `aroles` filter (middleware/jwt.go). Without it this
        // accepted any string the payload carried as a role, which made the
        // client looser than the gate — and removed the need for an `as
        // UserRole` cast that was papering over exactly that.
        return specialistRolesOf(user.additionalRoles).some((persona) =>
          (roles as readonly string[]).includes(persona),
        )
      },

      // Set tenant memberships
      setTenantMemberships: (memberships: TenantMembership[]) => {
        set({ tenantMemberships: memberships })
      },

      // Select a tenant after login (when requiresTenantSelection is true).
      // For platform memberships (tenantId is empty), the JWT from login is
      // already platform-scoped — no backend call needed. Just clear the
      // pending flag and let the caller navigate to /platform.
      selectTenant: async (tenantId: string) => {
        if (!tenantId) {
          // Platform membership — JWT already scoped from login
          resetClientState('tenant') // platform scope differs from any tenant (#301)
          set({ pendingTenantSelection: false, isLoading: false, error: null })
          return
        }
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.switchTenant({ tenantId })
          // Reset BEFORE the token swap: abort + drop everything issued under
          // the old tenant, then adopt the new token (#301).
          resetClientState('tenant')
          apiClient.setToken(response.accessToken)
          recordTenantSwitch(response.idleTimeout)
          set({
            user: response.user,
            token: response.accessToken,
            pendingTenantSelection: false,
            isLoading: false,
            error: null,
          })
        } catch (err) {
          const error = err as { message?: string }
          set({ isLoading: false, error: error.message || 'Failed to select tenant' })
          throw err
        }
      },

      // Switch tenant mid-session (from sidebar switcher).
      // After the backend issues a new access token scoped to the target tenant,
      // we store the token and the updated user. No header manipulation is needed:
      // the new JWT carries the correct tid claim, which the backend middleware
      // will use for all subsequent requests.
      //
      // DO NOT clear vharta-platform-context here. That store belongs exclusively
      // to platform admin impersonation flows. Clearing it during a regular tenant
      // switch corrupts the platform admin's cursor and removes the
      // X-Target-Tenant-ID header that platform routes depend on.
      // Platform context is cleared only on logout.
      switchTenant: async (tenantId: string) => {
        set({ isLoading: true, error: null })
        try {
          // tenantId "" signals platform context — backend handles this as
          // a switch to the platform-scoped JWT (tid="", is_platform=true).
          const response = await authApi.switchTenant({ tenantId })
          // Reset BEFORE the token swap: abort + drop everything issued under
          // the old tenant, then adopt the new token (#301).
          resetClientState('tenant')
          apiClient.setToken(response.accessToken)
          recordTenantSwitch(response.idleTimeout)
          set({
            user: response.user,
            token: response.accessToken,
            pendingTenantSelection: false,
            isLoading: false,
            error: null,
          })
        } catch (err) {
          const error = err as { message?: string }
          set({ isLoading: false, error: error.message || 'Failed to switch tenant' })
          throw err
        }
      },

      // Initialize auth state on app load
      initialize: async () => {
        const state = get()

        // Skip if already initialized - check both ways
        if (state._initialized) {
          return
        }

        // Mark as initialized IMMEDIATELY and don't proceed until this is set
        // This prevents any race conditions
        set({ _initialized: true })

        // Re-check after setting to prevent any concurrent calls
        const currentState = get()

        // Always try /auth/me — even without a local JWT. SSO flows land the
        // user with only an HttpOnly session cookie in the jar, no local
        // token. /auth/me accepts either Bearer header or vharta_session
        // cookie (auth middleware reads both). A 401 response falls through
        // to the "no session" path below.
        if (!currentState.isLoading) {
          set({ isLoading: true })
        }

        if (currentState.token) {
          apiClient.setToken(currentState.token)
        }

        try {
          // Validate token/cookie by calling /me
          const fetchedUser = await retryTransient(() => authApi.me())

          // Refresh the tenant list from /auth/me.
          //
          // tenantMemberships used to be written ONLY by login(). That was
          // survivable while an expired token on reload forced a logout and a
          // fresh login — the re-login repopulated it. Since initialize() began
          // recovering the session via the HttpOnly refresh cookie, a session
          // can stay authenticated indefinitely without ever calling login(),
          // which left this empty and made /select-tenant render zero
          // organizations (and silently flip its header, since the
          // has-platform-membership test is false for an empty array).
          //
          // Guarded: only overwrite when the server actually returned a list,
          // so a backend that omits the field cannot wipe good state.
          if (fetchedUser?.availableTenants && fetchedUser.availableTenants.length > 0) {
            set({ tenantMemberships: fetchedUser.availableTenants })
          }

          // Dev mode debugging: warn if persisted user differs from server response
          if (import.meta.env.DEV && currentState.user && currentState.user.email !== fetchedUser.email) {
            console.warn('[Auth] Server returned different user than persisted:', {
              persisted: currentState.user.email,
              server: fetchedUser.email,
              role: fetchedUser.role,
            })
          }

          // The server says a different user than the persisted one (another
          // tab or an expired session on a shared browser): nothing cached or
          // swept so far belongs to this identity (#301).
          if (currentState.user && currentState.user.id !== fetchedUser.id) {
            resetClientState('login')
          }

          // Only update if still initialized (not logged out during fetch)
          // ALWAYS trust the server response as source of truth
          if (get()._initialized) {
            set({
              user: fetchedUser,
              isAuthenticated: true,
              isLoading: false,
              // Server-authoritative — default to 'local' for backward compat
              // with backends that don't yet send this field.
              authMethod: fetchedUser.auth_method === 'sso' ? 'sso' : 'local',
            })

            // If the persisted token has no tenant context (single-tenant user
            // who logged in before the auto-switchTenant fix), upgrade it now.
            // Detect: user has a tenantId from /auth/me but current token has
            // no tenant segment (dev-token-email@ts format, not email|tenantId@ts).
            const restoredToken = get().token ?? ''
            const hasTenantInToken = restoredToken.includes('|')
            const memberships = get().tenantMemberships
            // Do NOT auto-switch for SSO sessions: the vharta_session cookie
            // already carries tenant_id server-side, so tenant routes work
            // without a re-minted token. Re-minting a LOCAL-issuer Bearer token
            // for an SSO user would shadow the SSO cookie (mw.Auth checks Bearer
            // first), making /auth/me report auth_method="local" on the next
            // load and making /sso/logout revoke the wrong (local) jti — i.e. it
            // would silently reintroduce the SSO-logout-never-revokes bug.
            // See docs/plans/AUTH_SESSION_HARDENING.md (A4/WS-5).
            if (
              !hasTenantInToken &&
              fetchedUser.tenantId &&
              memberships.length === 1 &&
              fetchedUser.role !== 'platform_admin' &&
              fetchedUser.auth_method !== 'sso'
            ) {
              try {
                const switchResp = await authApi.switchTenant({ tenantId: fetchedUser.tenantId })
                apiClient.setToken(switchResp.accessToken)
                recordTenantSwitch(switchResp.idleTimeout)
                set({
                  user: switchResp.user,
                  token: switchResp.accessToken,
                })
              } catch {
                // Non-fatal: user is authenticated, they may just see 400s on
                // tenant routes until they re-login.
              }
            }
          }
        } catch (initErr) {
          // Aborted because the identity changed under us: the new identity's
          // own flow owns the state now; do not touch it (#301).
          if (isStaleIdentityError(initErr)) return
          // A throttled or briefly unavailable /auth/me (429, 5xx, network) does
          // not say the session is gone, and the cookie refresh below would only
          // add load. Keep the persisted session as is; the next request's own
          // 401 recovery handles a genuinely lapsed token.
          if (isTransientAuthError(initErr)) {
            set({ isLoading: false, error: null })
            return
          }
          // /auth/me failed. Before giving up, try ONE cookie-backed refresh.
          //
          // This is the reload-forces-logout path: the access token has a 15-min
          // TTL (8 min for platform_admin/tenant_owner per idle_policy.go), so a
          // reload after a normal working gap arrives with an expired token and
          // no in-memory refresh token. Previously the catch wiped state
          // immediately and never attempted recovery, even though the HttpOnly
          // refresh cookie was sitting in the jar the whole time.
          //
          // Strictly one attempt, and only here at boot — refreshSession() logs
          // out on its own failure, so a bad cookie cannot loop.
          try {
            await get().refreshSession()
            // refreshSession() above already reconciled `user` via its own
            // best-effort /auth/me call — don't fetch it a second time here.
            set({
              isAuthenticated: true,
              isLoading: false,
              error: null,
            })
            return
          } catch (refreshErr) {
            if (isStaleIdentityError(refreshErr)) return
            // Refresh was throttled/unavailable, not rejected: keep the session.
            if (isTransientAuthError(refreshErr)) {
              set({ isLoading: false, error: null })
              return
            }
            // Fall through to the original clear-state path below.
          }

          // Token is invalid, clear auth state but DON'T reset _initialized
          // to prevent infinite loops. The _initialized flag should only be
          // reset on explicit logout, not on token validation failure.
          // Session is gone: drop its cached data (#301), but only when there WAS
          // an identity (not for every anonymous visitor) and refreshSession's own
          // logout() has not already reset.
          if ((currentState.user || currentState.token) && get().isAuthenticated) {
            resetClientState('logout')
          }
          apiClient.setToken(null)
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            authMethod: null,
          })
        }
      },
    }),
    {
      name: 'vharta-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        // WS-2 cookie-only mode: the access token is NOT persisted (in-memory
        // only) — removes the XSS localStorage-exfiltration vector. On reload the
        // session is re-established via the HttpOnly cookie in initialize()→
        // /auth/me (exactly like an SSO cookie session already does today).
        // Default (flag off): token persisted as before.
        ...(cookieAuthEnabled() ? {} : { token: state.token }),
        // refreshToken intentionally NOT persisted — storing it in localStorage
        // exposes it to XSS attacks. The session is re-established via the
        // HttpOnly session cookie or the user re-authenticates.
        isAuthenticated: state.isAuthenticated,
        tenantMemberships: state.tenantMemberships,
        pendingTenantSelection: state.pendingTenantSelection,
        // Persisted alongside `user` so a page reload keeps the last-known
        // auth method; initialize() always refreshes it from /auth/me.
        authMethod: state.authMethod,
      }),
    }
  )
)

/**
 * Selector — returns true when the current session was established via SSO.
 * Sourced from the server-authoritative `authMethod` state (populated from
 * GET /auth/me's `auth_method` field), not from decoding a local JWT.
 */
export const selectIsSSOSession = (state: { authMethod: 'sso' | 'local' | null }) =>
  state.authMethod === 'sso'
